import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [skuContainer] - The native block's optional SKU container
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['sku'];
  #target;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section, dialog');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    ++this.#generation;
  }

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    const picker =
      event.target instanceof Element ? event.target.closest('variant-picker, swatches-variant-picker-component') : null;
    if (picker?.dataset.productId && picker.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.#generation;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected || generation !== this.#generation) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (!newProduct && detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (resource) {
          const variantSku = resource.sku || '';

          if (this.hasAttribute('data-solstice-brand-sku')) {
            this.refs.sku.textContent = variantSku;
            this.refs.sku.hidden = !variantSku;
            return;
          }

          if (variantSku) {
            // Show the component and update the SKU
            this.style.display = 'block';
            this.refs.sku.textContent = variantSku;
          } else {
            // Hide the entire component when SKU is empty
            this.style.display = 'none';
            this.refs.sku.textContent = '';
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
